import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqs } from '@/mocks/faqs';
import Breadcrumb from '@/components/base/Breadcrumb';
import SeoMeta from '@/components/base/SeoMeta';
import JsonLd from '@/components/base/JsonLd';

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');

  return (
    <>
      <SeoMeta
        title="常见问题｜真相回旋镖怎么核实、怎么更正内容"
        description="关于真相回旋镖的常见疑问：内容与普通爆料号的区别、核实方式、人物匿名处理、内容更正与联系渠道，一篇讲清楚。"
        keywords="常见问题,如何核实爆料,辟谣澄清说明,内容更正,联系真相回旋镖"
        path="/faq"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }}
      />

      <section className="border-b border-background-300 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
            常见问题
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于我们如何选案例、如何核实、如何处理涉事人物信息，这里集中回答读者问得最多的问题。
          </p>
        </div>
      </section>

      <section className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-8">
          <div className="flex flex-col gap-3">
            {faqs.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.question}
                  className={`reveal overflow-hidden rounded-lg border transition-colors ${
                    isOpen ? 'border-primary-300 bg-primary-50/60' : 'border-background-300 bg-background-50'
                  }`}
                >
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 p-5 text-left md:p-6"
                  >
                    <span className="flex items-start gap-3">
                      <span className="mt-0.5 font-heading text-sm font-bold text-primary-600">
                        Q{index + 1}
                      </span>
                      <span className="font-heading text-base font-semibold text-foreground-950 md:text-lg">
                        {item.question}
                      </span>
                    </span>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                        isOpen ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600'
                      }`}
                    >
                      <i className={isOpen ? 'ri-subtract-line' : 'ri-add-line'} aria-hidden="true" />
                    </span>
                  </button>
                  {isOpen ? (
                    <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600 md:px-6 md:pb-6 md:pl-[3.4rem]">
                      {item.answer}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="reveal rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">还是没解决？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              直接发邮件说明你的问题，附上相关的原始链接或材料，我们会尽快回复。
            </p>
            <a
              href="mailto:hello@truth-boomerang.example"
              rel="nofollow"
              className="mt-4 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              写信给我们
            </a>
          </div>

          <div className="reveal mt-6 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">想先看案例？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              反转档案里按走向分类整理了全部案例，每条都附有完整时间线。
            </p>
            <Link
              to="/archive"
              className="mt-4 flex items-center justify-center gap-2 rounded-md border border-secondary-300 bg-background-50 px-4 py-2.5 text-sm font-label text-secondary-800 transition-colors hover:bg-secondary-100"
            >
              <i className="ri-folder-open-line" aria-hidden="true" />
              打开反转档案
            </Link>
          </div>
        </aside>
      </section>
    </>
  );
}